import { useAuthContext } from "@features/auth/contexts/AuthContext";
import type { MixedItemDist } from "@memorang/types";
import { useTranslation } from "react-i18next";
import type { RecommendedSkillTask } from "../../recommendations/adapters/dashboard-task-types";
import { useRecommendationFrontendContext } from "../useRecommendationFrontendContext";
import { useRecommendedStudyTasks } from "./useRecommendedStudyTasks";
import { useRecommendedTestInventory } from "./useRecommendedTestInventory";
import { useRecommendedTestTasks } from "./useRecommendedTestTasks";

type Params = {
	studyPackData?: MixedItemDist;
	isLoadingStudyPackData?: boolean;
	onSelectTags?: (tagIds: string[]) => void;
	onStartRecommendedSession?: (task: RecommendedSkillTask) => void;
};

export const useRecommendedTasksSection = ({
	studyPackData,
	isLoadingStudyPackData,
	onSelectTags,
	onStartRecommendedSession,
}: Params = {}) => {
	const { userInfo } = useAuthContext();
	const { t } = useTranslation();

	const testInventory = useRecommendedTestInventory();

	const {
		skills: skillTags,
		proficiencies: proficiencyTags,
		questionTypes: questionTypeTags,
	} = useRecommendationFrontendContext({
		studyPackData,
		additionalExamCollectionIds: testInventory.examCollectionIds,
	});

	const tests = useRecommendedTestTasks({
		skills: skillTags,
		preferredSkillIds: userInfo?.onboarding?.profile?.skill_ids,
		inventory: testInventory,
	});

	const studyTasks = useRecommendedStudyTasks({
		skills: skillTags,
		proficiencies: proficiencyTags,
		questionTypes: questionTypeTags,
		studyPackData,
		examCollectionIds: testInventory.examCollectionIds,
		preferredSkillIds: userInfo?.onboarding?.profile?.skill_ids,
		targetLevelId: userInfo?.onboarding?.profile?.target_level_id,
		onSelectTags,
		onStart: onStartRecommendedSession,
	});

	return {
		isEnabled: true,
		isLoading:
			testInventory.isLoading ||
			Boolean(isLoadingStudyPackData) ||
			studyTasks.isLoading,
		sectionTitle: t("dashboard.recommendedTasks.sectionTitle"),
		data: [
			...tests.tasks,
			...studyTasks.tasks.filter(
				(task) => !tests.coveredSkillIds.includes(task.recommendation.skill.id),
			),
		],
	};
};
